import React, { useState } from 'react';
import { 
  collection, 
  doc, 
  setDoc, 
  query, 
  where, 
  getDocs,
  serverTimestamp 
} from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { db, auth } from '../../../../firebase/config';
import './RejoinModal.scss';

const RejoinModal = ({ user, onClose, onSuccess }) => {
  const [newEmail, setNewEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    empID: user?.empID || '',
    designation: user?.designation || '',
    organogram: user?.organogram || 'technical',
    leaveEntitlement: user?.leaveEntitlement || 21,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!newEmail.trim()) {
      setError('New email is required');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setError('Invalid email format');
      return false;
    }

    if (!formData.empID.trim()) {
      setError('Employee ID is required');
      return false;
    }

    if (!formData.designation.trim()) {
      setError('Designation is required');
      return false;
    }

    if (password && password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }

    return true;
  };

  const checkEmailExists = async (email) => {
    try {
      const myelinersRef = collection(db, 'myeliners');
      const emailQuery = query(myelinersRef, where('email', '==', email));
      const emailSnapshot = await getDocs(emailQuery);
      return !emailSnapshot.empty;
    } catch (err) {
      console.error('Error checking email:', err);
      return false;
    }
  };

  const checkEmpIDExists = async (empID) => {
    try {
      const myelinersRef = collection(db, 'myeliners');
      const empIDQuery = query(myelinersRef, where('empID', '==', empID));
      const empIDSnapshot = await getDocs(empIDQuery);
      return !empIDSnapshot.empty;
    } catch (err) {
      console.error('Error checking empID:', err);
      return false;
    }
  };

  const handleRejoin = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      // Check if new email already exists
      const emailExists = await checkEmailExists(newEmail);
      if (emailExists) {
        setError('This email already exists in the system');
        setLoading(false);
        return;
      }

      // Check if empID already exists (if it's different from archived user's empID)
      if (formData.empID !== user?.empID) {
        const empIDExists = await checkEmpIDExists(formData.empID);
        if (empIDExists) {
          setError('This Employee ID already exists in the system');
          setLoading(false);
          return;
        }
      }

      // Create auth account if password is provided
      if (password) {
        try {
          await createUserWithEmailAndPassword(auth, newEmail, password);
        } catch (authError) {
          if (authError.code === 'auth/email-already-in-use') {
            setError('An account with this email already exists');
            setLoading(false);
            return;
          }
          throw authError;
        }
      }

      // Prepare employee data from archived user
      const employeeData = {
        // Basic Information
        name: user?.name || '',
        email: newEmail,
        personalEmail: user?.personalEmail || '',
        empID: formData.empID,
        designation: formData.designation,
        organogram: formData.organogram,
        dob: user?.dob || null,
        phone: user?.phone || '',
        whatsApp: user?.whatsApp || '',
        address: user?.address || '',
        country: user?.country || '',
        location: user?.location || '',
        maritalStatus: user?.maritalStatus || '',
        
        // Emergency Contact
        emergencyContact: user?.emergencyContact || '',
        emergencyNumber: user?.emergencyNumber || '',
        emergencyRelation: user?.emergencyRelation || '',
        
        // Language Preferences
        preferredLang: user?.preferredLang || '',
        allLang: user?.allLang || '',
        
        // Leave Information
        leaveEntitlement: parseInt(formData.leaveEntitlement || 21, 10),
        leaveBalance: parseInt(formData.leaveEntitlement || 21, 10),
        leaveTaken: 0,
        
        // Profile Information
        img: user?.img || '',
        publicDescription: user?.publicDescription || '',
        shortDescription: user?.shortDescription || '',
        shortProfileBrief: user?.shortProfileBrief || '',
        
        // Social Links
        socialLinkedIn: user?.socialLinkedIn || '',
        socialInstagram: user?.socialInstagram || '',
        socialTwitter: user?.socialTwitter || '',
        socialWeb: user?.socialWeb || '',
        
        // System Fields
        userType: user?.userType || 'user',
        myelinLive: true,
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
        joinDate: new Date().toISOString().split('T')[0],
        
        // Track rejoin information
        rejoinedFrom: user?.email || user?.id,
        rejoinedAt: serverTimestamp(),
        previousArchivedDate: user?.archived || null,
      };

      // Create document in myeliners collection using new email as document ID
      const employeeRef = doc(db, 'myeliners', newEmail);
      await setDoc(employeeRef, employeeData);

      console.log('✅ User rejoined successfully:', newEmail);

      // Call success callback
      if (onSuccess) {
        onSuccess(newEmail);
      }

      // Close modal
      onClose();
    } catch (err) {
      console.error('Error rejoining user:', err);
      setError(err.message || 'Failed to rejoin user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rejoin-modal-overlay">
      <div className="rejoin-modal">
        <div className="modal-header">
          <h2>Rejoin User</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleRejoin}>
          <div className="modal-body">
            <div className="user-info-section">
              <h3>Archived User Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>Name</label>
                  <p>{user?.name || 'N/A'}</p>
                </div>
                <div className="info-item">
                  <label>Previous Email</label>
                  <p>{user?.email || user?.id || 'N/A'}</p>
                </div>
                <div className="info-item">
                  <label>Previous Employee ID</label>
                  <p>{user?.empID || 'N/A'}</p>
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>New User Information</h3>
              
              <div className="form-group">
                <label htmlFor="newEmail">
                  New Email <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="newEmail"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Enter new email address"
                  required
                  disabled={loading}
                />
                <small>The user will be created with this new email address</small>
              </div>

              <div className="form-group">
                <label htmlFor="empID">
                  Employee ID <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="empID"
                  name="empID"
                  value={formData.empID}
                  onChange={handleInputChange}
                  placeholder="E.g. E-AB-0224"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="designation">
                  Designation <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="designation"
                  name="designation"
                  value={formData.designation}
                  onChange={handleInputChange}
                  placeholder="E.g. Software Engineer"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="organogram">Department</label>
                <select
                  id="organogram"
                  name="organogram"
                  value={formData.organogram}
                  onChange={handleInputChange}
                  disabled={loading}
                >
                  <option value="technical">Technical</option>
                  <option value="management">Management</option>
                  <option value="hr">HR</option>
                  <option value="marketing">Marketing</option>
                  <option value="finance">Finance</option>
                  <option value="operations">Operations</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="leaveEntitlement">Annual Leave Entitlement</label>
                <input
                  type="number"
                  id="leaveEntitlement"
                  name="leaveEntitlement"
                  value={formData.leaveEntitlement}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Initial Password (Optional)</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Leave blank to send setup email"
                  disabled={loading}
                />
                <small>If provided, an auth account will be created. Otherwise, user will need to sign up.</small>
              </div>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
          </div>
          
          <div className="modal-footer">
            <button 
              type="button" 
              className="cancel-button" 
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="submit-button"
              disabled={loading}
            >
              {loading ? 'Rejoining...' : 'Rejoin User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RejoinModal;
